import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Lock, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import SEO from "@/components/SEO";
import { useCustomer } from "@/context/CustomerAuthContext";
import { BRAND } from "@/lib/brand";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const { items, totals, clear } = useCart();
  const { customer, signInWithGoogle } = useCustomer();
  const nav = useNavigate();
  const [form, setForm] = useState({ customer_name: "", phone: "", email: "", address: "", city: "", pincode: "", notes: "" });
  const [errors, setErrors] = useState({});
  const [placing, setPlacing] = useState(false);
  const [config, setConfig] = useState({ razorpay_enabled: false });
  const [placed, setPlaced] = useState(null); // { order_id, wa_message }
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    api.get("/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  // Prefill on mount from localStorage (guest) — signed-in customer prefill runs next
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("sj_ship_addr") || "null");
      if (saved && !prefilled) {
        setForm((f) => ({ ...f, ...saved, notes: f.notes }));
        setPrefilled(true);
      }
    } catch (err) {
      // localStorage may be blocked (private mode) or contain stale JSON.
      // Recoverable — user just re-enters the address — but log so we don't
      // silently swallow issues in production.
      // eslint-disable-next-line no-console
      console.warn("Checkout: could not restore saved shipping address", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prefill from signed-in customer's saved shipping address (takes precedence)
  useEffect(() => {
    if (!customer) return;
    if (customer.shipping_address) {
      setForm((f) => ({
        ...f,
        customer_name: customer.shipping_address.customer_name || f.customer_name,
        phone: customer.shipping_address.phone || f.phone,
        email: customer.shipping_address.email || f.email || customer.email || "",
        address: customer.shipping_address.address || f.address,
        city: customer.shipping_address.city || f.city,
        pincode: customer.shipping_address.pincode || f.pincode,
      }));
      setPrefilled(true);
    } else {
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || customer.name || "",
        email: f.email || customer.email || "",
      }));
    }
  }, [customer]);

  const clearSavedAddress = () => {
    localStorage.removeItem("sj_ship_addr");
    setForm({ customer_name: customer?.name || "", phone: "", email: customer?.email || "", address: "", city: "", pincode: "", notes: "" });
    setPrefilled(false);
    toast.message("Saved address cleared");
  };

  const shipping = totals.subtotal >= 1499 ? 0 : 79;
  const total = totals.subtotal + shipping;

  const validate = () => {
    const e = {};
    if (!form.customer_name.trim()) e.customer_name = "Please enter your name";
    if (!/^[0-9+\-\s]{10,15}$/.test(form.phone.trim())) e.phone = "Enter a valid phone number";
    if (!form.address.trim()) e.address = "Address is required";
    if (!form.city.trim()) e.city = "City is required";
    if (!/^[0-9]{5,6}$/.test(form.pincode.trim())) e.pincode = "Enter a valid pincode";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return toast.error("Your bag is empty");
    if (!validate()) return toast.error("Please fix the highlighted fields");

    setPlacing(true);
    try {
      const payload = {
        ...form,
        items: items.map(i => ({ product_id: i.product_id, name: i.name, price: i.price, quantity: i.quantity, image: i.image })),
        total,
        payment_method: config.razorpay_enabled ? "razorpay" : "pending",
      };
      const { data } = await api.post("/orders", payload);

      // Save shipping address in browser for future visits (guest + signed-in)
      try {
        localStorage.setItem("sj_ship_addr", JSON.stringify({
          customer_name: form.customer_name,
          phone: form.phone,
          email: form.email,
          address: form.address,
          city: form.city,
          pincode: form.pincode,
        }));
      } catch (err) {
        // Non-blocking — checkout continues even if we can't persist the address.
        // eslint-disable-next-line no-console
        console.warn("Checkout: could not persist shipping address to localStorage", err);
      }

      if (config.razorpay_enabled && data.razorpay_order_id) {
        const ok = await loadRazorpayScript();
        if (!ok) throw new Error("Failed to load payment gateway");

        const options = {
          key: data.razorpay_key_id,
          amount: data.amount,
          currency: data.currency,
          name: "Star Jewels",
          description: `Order ${data.order_id.slice(0, 8)}`,
          order_id: data.razorpay_order_id,
          prefill: { name: form.customer_name, email: form.email || "", contact: form.phone },
          theme: { color: "#D4AF37" },
          handler: async (rp) => {
            try {
              await api.post("/orders/verify", {
                order_id: data.order_id,
                razorpay_order_id: rp.razorpay_order_id,
                razorpay_payment_id: rp.razorpay_payment_id,
                razorpay_signature: rp.razorpay_signature,
              });
              clear();
              setPlaced({ order_id: data.order_id });
              toast.success("Payment successful!");
            } catch (err) {
              toast.error("Payment verification failed. Please email us at " + BRAND.email + ".");
            }
          },
          modal: { ondismiss: () => toast.message("Payment cancelled") },
        };
        const rp = new window.Razorpay(options);
        // Surface Razorpay-level errors (network drop, gateway decline, expired
        // OTP, etc.) so the shopper doesn't just see the modal disappear.
        rp.on("payment.failed", (evt) => {
          const err = evt?.error || {};
          // eslint-disable-next-line no-console
          console.warn("Razorpay payment.failed:", err);
          toast.error(err.description || err.reason || "Payment failed. Please try again.");
        });
        rp.open();
      } else {
        // Fallback: order placed without payment; team follows up by email.
        clear();
        setPlaced({ order_id: data.order_id });
        toast.success("Order placed! Our team will confirm shortly.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Something went wrong");
    } finally {
      setPlacing(false);
    }
  };

  if (placed) {
    return (
      <main className="mx-auto max-w-2xl px-5 sm:px-10 py-24 text-center" data-testid="checkout-success">
        <CheckCircle2 size={48} className="mx-auto text-sj-goldDark" />
        <h1 className="font-display text-3xl sm:text-4xl mt-6 font-light">Thank you! ✨</h1>
        <div className="mt-2 text-sj-muted text-sm">Order ID: <span className="text-sj-ink font-medium" data-testid="success-order-id">{placed.order_id}</span></div>
        <p className="mt-6 text-sj-inkSoft leading-relaxed">
          We’ve received your order. Our team will send a confirmation to your email shortly — you can also track it under <Link to="/account" className="text-sj-goldDark underline underline-offset-4">My Orders</Link>.
        </p>
        <div className="mt-10">
          <Link to="/shop" className="text-sm text-sj-muted hover:text-sj-goldDark" data-testid="success-continue-shopping">Continue shopping</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-[1440px] px-5 sm:px-10 py-14 sm:py-20" data-testid="checkout-page">
      <SEO title="Checkout" description="Complete your Star Jewels order — secure payment via Razorpay, free shipping over ₹1,499." path="/checkout" noindex />
      <Link to="/cart" className="inline-flex items-center gap-2 text-sm text-sj-muted hover:text-sj-goldDark mb-6">
        <ChevronLeft size={16} /> Back to bag
      </Link>
      <h1 className="font-display text-4xl sm:text-5xl font-light">Checkout</h1>

      {!customer && items.length > 0 && (
        <div className="mt-6 p-4 bg-sj-cream rounded-md flex items-center justify-between gap-4 flex-wrap" data-testid="checkout-signin-nudge">
          <div className="text-sm text-sj-inkSoft">
            <span className="font-medium text-sj-ink">Have an account?</span> Sign in with Google to check out faster & track your orders.
          </div>
          <button onClick={signInWithGoogle} className="btn-outline text-sm" data-testid="checkout-signin-btn">
            <svg width="14" height="14" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.1 5.9 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.1 5.9 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.2 2.4-5.3 0-9.7-3.3-11.3-8L6.2 32.9C9.5 39.5 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.2c-.4.4 6.7-4.9 6.7-14.8 0-1.2-.1-2.3-.5-3.5z"/></svg>
            Sign in with Google
          </button>
        </div>
      )}

      {items.length === 0 ? (
        <div className="mt-12 text-sj-muted">Your bag is empty. <Link to="/shop" className="text-sj-goldDark underline underline-offset-4">Browse the shop</Link>.</div>
      ) : (
        <form onSubmit={submit} className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="overline">Contact & Shipping</div>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                ["customer_name", "Full name", "text", "sm:col-span-2"],
                ["phone", "Phone", "tel", ""],
                ["email", "Email (optional)", "email", ""],
                ["address", "Address (house, street)", "text", "sm:col-span-2"],
                ["city", "City", "text", ""],
                ["pincode", "Pincode", "text", ""],
              ].map(([name, label, type, extra]) => (
                <div key={name} className={extra}>
                  <label className="text-xs text-sj-inkSoft" htmlFor={name}>{label}</label>
                  <input
                    id={name}
                    type={type}
                    value={form[name]}
                    onChange={(e) => setForm((f) => ({ ...f, [name]: e.target.value }))}
                    className={`mt-1.5 w-full bg-white border ${errors[name] ? "border-sj-rose" : "border-sj-ink/15"} focus:border-sj-goldDark outline-none rounded-md px-4 h-11 text-sm transition-colors`}
                    data-testid={`checkout-${name.replace("_", "-")}`}
                  />
                  {errors[name] && <div className="mt-1 text-xs text-sj-rose">{errors[name]}</div>}
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="text-xs text-sj-inkSoft" htmlFor="notes">Order notes (optional)</label>
                <textarea
                  id="notes"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  className="mt-1.5 w-full bg-white border border-sj-ink/15 focus:border-sj-goldDark outline-none rounded-md px-4 py-3 text-sm"
                  data-testid="checkout-notes"
                />
              </div>
            </div>
            {prefilled && (
              <div className="mt-3 flex items-center justify-between text-xs text-sj-muted" data-testid="checkout-prefilled-note">
                <span>✓ Address auto-filled from your last order.</span>
                <button type="button" onClick={clearSavedAddress} className="underline underline-offset-4 hover:text-sj-goldDark" data-testid="checkout-clear-address">
                  Use a different address
                </button>
              </div>
            )}

            <div className="mt-8 p-5 rounded-md bg-sj-cream flex items-start gap-3">
              <Lock size={16} className="text-sj-goldDark mt-0.5 shrink-0" />
              <div className="text-sm text-sj-inkSoft">
                {config.razorpay_enabled
                  ? "You'll be redirected to Razorpay to complete a secure payment. UPI, cards, netbanking accepted."
                  : "Payment gateway will be enabled soon. Your order will be placed and our team will confirm via email."}
              </div>
            </div>
          </div>

          <aside className="lg:col-span-5 lg:sticky lg:top-24 h-fit bg-white border border-sj-ink/10 rounded-md p-6 sm:p-8">
            <div className="overline">Your order</div>
            <ul className="mt-4 space-y-3 max-h-64 overflow-auto pr-1">
              {items.map((it) => (
                <li key={it.product_id} className="flex justify-between text-sm">
                  <span className="text-sj-inkSoft line-clamp-1 pr-3">{it.name} × {it.quantity}</span>
                  <span>₹{(it.price * it.quantity).toLocaleString("en-IN")}</span>
                </li>
              ))}
            </ul>
            <div className="h-px bg-sj-ink/10 my-5" />
            <div className="text-sm space-y-2">
              <div className="flex justify-between"><span className="text-sj-inkSoft">Subtotal</span><span>₹{totals.subtotal.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between"><span className="text-sj-inkSoft">Shipping</span><span>{shipping === 0 ? "Free" : `₹${shipping}`}</span></div>
            </div>
            <div className="h-px bg-sj-ink/10 my-5" />
            <div className="flex justify-between items-baseline">
              <span className="font-display text-lg">Total</span>
              <span className="font-display text-2xl" data-testid="checkout-total">₹{total.toLocaleString("en-IN")}</span>
            </div>
            <button
              type="submit"
              disabled={placing}
              className="btn-primary w-full justify-center mt-6 disabled:opacity-60"
              data-testid="checkout-place-order"
            >
              {placing ? "Placing order…" : config.razorpay_enabled ? "Pay with Razorpay" : "Place order"}
            </button>
            <p className="text-xs text-sj-muted mt-4 text-center">
              By placing this order, you'll receive a confirmation email at <span className="text-sj-ink font-medium">{BRAND.email}</span>.
            </p>
          </aside>
        </form>
      )}
    </main>
  );
}
